import {
  getSkill,
  type ActiveGoal,
  type CoachAction,
  type ContactTimelineItem,
  type FeedItem,
  type GrowthTask,
  type JournalEntry,
  type NetworkContact,
} from "@/data/prooftree"
import {
  createEqTask,
  getAppSkillId,
  getSeedSkillId,
  isSupabaseUuid,
  parseCheckpointLevel,
  type BackendWriteResult,
} from "@/services/prooftreeBackend"
import { getSupabaseClient } from "@/services/supabaseClient"

export { createEqTask } from "@/services/prooftreeBackend"

export type EqVisibility = "private" | "group" | "public"
type BackendMode = "supabase" | "local"

export type EqGoalInput = {
  title: string
  context?: string
  dueOn?: string | null
  skillIds?: string[]
  visibility?: EqVisibility
}

export type EqContactInput = {
  name: string
  role?: string
  relationship?: string
  frequency?: string
  context?: string
}

export type EqJournalEntryInput = {
  title?: string
  reflectionText: string
  skillIds?: string[]
  proofId?: string | null
  taskId?: string | null
  goalId?: string | null
  contactId?: string | null
  visibility?: EqVisibility
  videoUrl?: string | null
  storagePath?: string | null
  occurredOn?: string | null
}

export type EqContactInteractionInput = {
  contactId: string
  title: string
  notes?: string
  mood?: "positive" | "neutral" | "mixed" | "negative"
  skillScores?: {
    skillId: string
    score: number
    checkpointLevel?: string | number | null
    label?: string
  }[]
  taskId?: string | null
  proofId?: string | null
  journalEntryId?: string | null
  occurredAt?: string | null
}

export type EqCoachActionInput = {
  skillId: string
  title: string
  description?: string
  goalId?: string | null
  contactId?: string | null
  templateId?: string | null
  status?: "suggested" | "saved" | "task_created" | "completed" | "archived"
  detailedPlan?: Record<string, unknown>
}

export type EqCoachTaskInput = EqCoachActionInput & {
  taskTitle: string
  level?: string | number
  dueOn?: string | null
  visibility?: EqVisibility
}

export type EqTaskCompletionInput = {
  taskId: string
  reflectionText?: string
  visibility?: EqVisibility
  completedAt?: string
}

export type EqGrowthCirclePostInput = {
  title: string
  summary: string
  journalEntryId?: string | null
  proofId?: string | null
  visibility?: "group" | "public"
}

export type EqGrowthCircleReactionInput = {
  postId: string
  reaction?: string
}

export type EqGrowthCircleCommentInput = {
  postId: string
  body: string
}

export type EqReadResult<T> = {
  mode: BackendMode
  items: T[]
  message: string
}

export type EqDailyGrowthSummary = {
  date: string
  completedCount: number
  skillIds: string[]
  updatedAt?: string
}

export type EqContactSkillTrend = {
  skillId: string
  currentScore: number
  changePercent: number
  direction: "positive" | "negative" | "neutral"
  history: {
    id: string
    date: string
    score: number
    label: string
    checkpointLevel: number | null
  }[]
}

export async function listEqContactInteractions(
  contactId: string,
): Promise<EqReadResult<ContactTimelineItem>> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client)
    return readResult("local", [], "Supabase is not configured for contact interactions.")
  if (!user) return readResult("local", [], authError ?? "Sign in to sync contact interactions.")
  if (!isUuid(contactId)) {
    return readResult(
      "local",
      [],
      "Contact interaction history stays local until this contact has a Supabase id.",
    )
  }

  const { data, error } = await client
    .from("eq_contact_interactions")
    .select("id,title,notes,mood,occurred_at,created_at")
    .eq("contact_id", contactId)
    .order("occurred_at", { ascending: false })
    .limit(12)

  if (error) return readResult("local", [], `Contact interaction sync failed: ${error.message}`)

  const rows = Array.isArray(data) ? data.map(asRecord) : []
  return readResult(
    "supabase",
    rows.map((row) => ({
      id: asString(row.id),
      type: "Event",
      title: asString(row.title),
      meta: dateKey(row.occurred_at || row.created_at),
      status: asString(row.mood) || "Recorded",
    })),
    "Contact interaction history loaded from Supabase.",
  )
}

function localResult(message: string, reason: BackendWriteResult["reason"] = "missing-config") {
  return {
    mode: "local",
    persisted: false,
    reason,
    message,
  } satisfies BackendWriteResult
}

async function getSignedInUser() {
  const client = getSupabaseClient()

  if (!client) {
    return {
      client: null,
      user: null,
      error: "Supabase is not configured for this build.",
    }
  }

  const { data, error } = await client.auth.getUser()
  if (error || !data.user) {
    return {
      client,
      user: null,
      error: "Sign in before saving this EQ data to Supabase.",
    }
  }

  return { client, user: data.user, error: null }
}

function normalizeSkillIds(skillIds: string[] | undefined) {
  return Array.from(new Set((skillIds ?? []).filter(Boolean).map(getSeedSkillId)))
}

function isUuid(value: string | null | undefined) {
  return isSupabaseUuid(value)
}

function readResult<T>(mode: BackendMode, items: T[], message: string): EqReadResult<T> {
  return { mode, items, message }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function asString(value: unknown) {
  return typeof value === "string" ? value : ""
}

function asNumber(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0
}

function dateKey(value: unknown) {
  const source = asString(value)
  return source ? source.slice(0, 10) : new Date().toISOString().slice(0, 10)
}

function taskStatus(value: string): GrowthTask["status"] {
  if (value === "completed") return "Completed"
  if (value === "archived") return "Linked"
  return "Active"
}

function dueLabel(value: unknown) {
  const source = asString(value)
  if (!source) return "Soon"
  return source.slice(0, 10)
}

function stringArray(value: unknown) {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : []
}

function planStringArray(plan: Record<string, unknown>, key: string, fallback: string[]) {
  const value = stringArray(plan[key])
  return value.length > 0 ? value : fallback
}

function coachActionFromRow(row: Record<string, unknown>): CoachAction {
  const skillId = getAppSkillId(asString(row.skill_id))
  const skill = getSkill(skillId)
  const detailedPlan = asRecord(row.detailed_plan)

  return {
    id: asString(row.id),
    skillId,
    title: asString(row.title),
    description: asString(row.description),
    contactId: asString(row.contact_id) || undefined,
    goalId: asString(row.goal_id) || undefined,
    timing: asString(detailedPlan.timing) || "Soon",
    effort: asString(detailedPlan.effort) || "10 min",
    quickTip: asString(detailedPlan.quickTip) || "Choose one visible behavior and capture a note.",
    mindset:
      asString(detailedPlan.mindset) ||
      `Treat this as a small ${skill.title} rep, not a performance.`,
    phrases: planStringArray(detailedPlan, "phrases", [
      `I want to practice ${skill.title} here, so let me slow down for a moment.`,
    ]),
    preparation: planStringArray(detailedPlan, "preparation", [
      "Write the desired outcome in one sentence before you start.",
    ]),
    expectedOutcome:
      asString(detailedPlan.expectedOutcome) ||
      `You complete one real-world ${skill.title} action and have a note that can become evidence.`,
    taskTitle: asString(detailedPlan.taskTitle) || asString(row.title),
  }
}

export async function listEqTasks(): Promise<EqReadResult<GrowthTask>> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client) return readResult("local", [], "Supabase is not configured for tasks.")
  if (!user) return readResult("local", [], authError ?? "Sign in to sync tasks.")

  const { data, error } = await client
    .from("eq_tasks")
    .select(
      "id,title,skill_id,checkpoint_level,status,reflection,visibility,due_on,completed_at,goal_id,contact_id,coach_action_id,updated_at",
    )
    .neq("status", "archived")
    .order("updated_at", { ascending: false })

  if (error) return readResult("local", [], `Task sync failed: ${error.message}`)

  const rows = Array.isArray(data) ? data.map(asRecord) : []
  const taskIds = rows.map((row) => asString(row.id)).filter(isUuid)
  const goalIds = rows.map((row) => asString(row.goal_id)).filter(isUuid)
  const skillIdsByTask = new Map<string, string[]>()
  const goalTitles = new Map<string, string>()

  if (taskIds.length > 0) {
    const { data: skillRows } = await client
      .from("eq_task_skills")
      .select("task_id,skill_id")
      .in("task_id", taskIds)

    if (Array.isArray(skillRows)) {
      skillRows.map(asRecord).forEach((row) => {
        const taskId = asString(row.task_id)
        const skillId = getAppSkillId(asString(row.skill_id))
        if (!taskId || !skillId) return
        skillIdsByTask.set(taskId, [...(skillIdsByTask.get(taskId) ?? []), skillId])
      })
    }
  }

  if (goalIds.length > 0) {
    const { data: goalRows } = await client.from("eq_goals").select("id,title").in("id", goalIds)

    if (Array.isArray(goalRows)) {
      goalRows.map(asRecord).forEach((row) => {
        const id = asString(row.id)
        const title = asString(row.title)
        if (id && title) goalTitles.set(id, title)
      })
    }
  }

  return readResult(
    "supabase",
    rows.map((row) => {
      const id = asString(row.id)
      const primarySkillId = getAppSkillId(asString(row.skill_id))
      const skill = getSkill(primarySkillId)
      const skillIds = skillIdsByTask.get(id) ?? [primarySkillId]
      const completedAt = asString(row.completed_at)
      const reflection = asString(row.reflection)
      const visibility = (asString(row.visibility) as EqVisibility) || "private"

      return {
        id,
        title: asString(row.title),
        goal: goalTitles.get(asString(row.goal_id)) ?? "Coach Mode practice",
        domainId: skill.domainId,
        skillIds,
        suggestedSkillIds: [],
        contactId: asString(row.contact_id) || undefined,
        checkpoint: `Level ${asNumber(row.checkpoint_level) || 1}`,
        due: dueLabel(row.due_on),
        status: taskStatus(asString(row.status)),
        completedAt: completedAt ? completedAt.slice(0, 10) : undefined,
        nextStep: reflection || "Complete the action, then capture one short reflection.",
        reflection: reflection
          ? {
              text: reflection,
              date: completedAt ? completedAt.slice(0, 10) : dateKey(row.updated_at),
              visibility,
            }
          : undefined,
      }
    }),
    "Tasks loaded from Supabase.",
  )
}

export async function listEqGoals(): Promise<EqReadResult<ActiveGoal>> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client) return readResult("local", [], "Supabase is not configured for goals.")
  if (!user) return readResult("local", [], authError ?? "Sign in to sync goals.")

  const { data, error } = await client
    .from("eq_goals")
    .select("id,title,context,due_on,status,updated_at")
    .is("archived_at", null)
    .neq("status", "archived")
    .order("updated_at", { ascending: false })

  if (error) return readResult("local", [], `Goal sync failed: ${error.message}`)

  const rows = Array.isArray(data) ? data.map(asRecord) : []
  const goalIds = rows.map((row) => asString(row.id)).filter(isUuid)
  const skillIdsByGoal = new Map<string, string[]>()

  if (goalIds.length > 0) {
    const { data: skillRows } = await client
      .from("eq_goal_skills")
      .select("goal_id,skill_id")
      .in("goal_id", goalIds)

    if (Array.isArray(skillRows)) {
      skillRows.map(asRecord).forEach((row) => {
        const goalId = asString(row.goal_id)
        const skillId = getAppSkillId(asString(row.skill_id))
        if (!goalId || !skillId) return
        skillIdsByGoal.set(goalId, [...(skillIdsByGoal.get(goalId) ?? []), skillId])
      })
    }
  }

  return readResult(
    "supabase",
    rows.map((row) => {
      const id = asString(row.id)
      const skillIds = skillIdsByGoal.get(id) ?? []

      return {
        id,
        title: asString(row.title),
        context: asString(row.context) || "Saved for EQ practice planning.",
        due: dueLabel(row.due_on),
        skillIds: skillIds.length > 0 ? skillIds : ["active_listening_presence"],
      }
    }),
    "Goals loaded from Supabase.",
  )
}

export async function listEqContacts(): Promise<EqReadResult<NetworkContact>> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client) return readResult("local", [], "Supabase is not configured for contacts.")
  if (!user) return readResult("local", [], authError ?? "Sign in to sync contacts.")

  const { data, error } = await client
    .from("eq_contacts")
    .select("id,name,role,relationship,frequency,context,avatar_url,updated_at")
    .is("archived_at", null)
    .order("updated_at", { ascending: false })

  if (error) return readResult("local", [], `Contact sync failed: ${error.message}`)

  const rows = Array.isArray(data) ? data.map(asRecord) : []
  const contactIds = rows.map((row) => asString(row.id)).filter(isUuid)
  const skillIdsByContact = new Map<string, Set<string>>()

  if (contactIds.length > 0) {
    const { data: historyRows } = await client
      .from("eq_contact_skill_history")
      .select("contact_id,skill_id")
      .in("contact_id", contactIds)

    if (Array.isArray(historyRows)) {
      historyRows.map(asRecord).forEach((row) => {
        const contactId = asString(row.contact_id)
        const skillId = getAppSkillId(asString(row.skill_id))
        if (!contactId || !skillId) return

        const current = skillIdsByContact.get(contactId) ?? new Set<string>()
        current.add(skillId)
        skillIdsByContact.set(contactId, current)
      })
    }
  }

  return readResult(
    "supabase",
    rows.map((row) => {
      const id = asString(row.id)
      const skillIds = Array.from(skillIdsByContact.get(id) ?? [])

      return {
        id,
        name: asString(row.name),
        role: asString(row.role) || "Personal contact",
        frequency: asString(row.frequency) || "Manual",
        relationship: asString(row.relationship) || "Saved contact",
        context: asString(row.context) || "Available for Coach Mode suggestions.",
        avatar: asString(row.avatar_url) || undefined,
        skillIds:
          skillIds.length > 0
            ? skillIds
            : ["active_listening_presence", "clear_communication_requests"],
      }
    }),
    "Contacts loaded from Supabase.",
  )
}

export async function listEqJournalEntries(options?: {
  contactId?: string | null
}): Promise<EqReadResult<JournalEntry>> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client) return readResult("local", [], "Supabase is not configured for Journal entries.")
  if (!user) return readResult("local", [], authError ?? "Sign in to sync Journal entries.")

  let query = client
    .from("eq_journal_entries")
    .select("id,title,reflection_text,video_url,visibility,occurred_on,contact_id,task_id")
    .order("occurred_on", { ascending: false })
    .order("created_at", { ascending: false })

  if (isUuid(options?.contactId)) {
    query = query.eq("contact_id", options.contactId)
  }

  const { data, error } = await query
  if (error) return readResult("local", [], `Journal sync failed: ${error.message}`)

  const rows = Array.isArray(data) ? data.map(asRecord) : []
  const entryIds = rows.map((row) => asString(row.id)).filter(isUuid)
  const skillIdsByEntry = new Map<string, string[]>()

  if (entryIds.length > 0) {
    const { data: skillRows } = await client
      .from("eq_journal_entry_skills")
      .select("journal_entry_id,skill_id")
      .in("journal_entry_id", entryIds)

    if (Array.isArray(skillRows)) {
      skillRows.map(asRecord).forEach((row) => {
        const entryId = asString(row.journal_entry_id)
        const skillId = getAppSkillId(asString(row.skill_id))
        if (!entryId || !skillId) return
        skillIdsByEntry.set(entryId, [...(skillIdsByEntry.get(entryId) ?? []), skillId])
      })
    }
  }

  return readResult(
    "supabase",
    rows.map((row) => {
      const id = asString(row.id)
      const title = asString(row.title)
      const contactId = asString(row.contact_id)

      return {
        id,
        taskId: asString(row.task_id) || id,
        taskTitle: title || "Supabase Journal entry",
        date: dateKey(row.occurred_on),
        skillIds: skillIdsByEntry.get(id) ?? ["active_listening_presence"],
        contactIds: contactId ? [contactId] : undefined,
        text: asString(row.reflection_text),
        videoThumbnail: asString(row.video_url) || undefined,
        visibility: (asString(row.visibility) as EqVisibility) || "private",
      }
    }),
    "Journal entries loaded from Supabase.",
  )
}

export async function listEqGrowthCirclePosts(): Promise<EqReadResult<FeedItem>> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client) return readResult("local", [], "Supabase is not configured for Growth Circle.")
  if (!user) return readResult("local", [], authError ?? "Sign in to sync Growth Circle.")

  const { data, error } = await client
    .from("eq_growth_circle_posts")
    .select("id,title,summary,visibility,created_at,user_id")
    .eq("status", "active")
    .order("created_at", { ascending: false })

  if (error) return readResult("local", [], `Growth Circle sync failed: ${error.message}`)

  const rows = Array.isArray(data) ? data.map(asRecord) : []
  return readResult(
    "supabase",
    rows.map((row) => ({
      id: asString(row.id),
      person: asString(row.user_id) === user.id ? "You" : "Growth Circle member",
      role: "Growth Circle",
      date: dateKey(row.created_at),
      title: asString(row.title),
      summary: asString(row.summary),
      skillIds: ["active_listening_presence"],
      visibility: (asString(row.visibility) as "group" | "public") || "group",
      likes: 0,
      comments: 0,
    })),
    "Growth Circle posts loaded from Supabase.",
  )
}

export async function createEqGoal(input: EqGoalInput): Promise<BackendWriteResult> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client) return localResult("Goal kept in the local session. Supabase is not configured.")
  if (!user) return localResult(authError ?? "Sign in before saving goals.", "auth-required")

  const { data, error } = await client
    .from("eq_goals")
    .insert({
      user_id: user.id,
      title: input.title.trim(),
      context: input.context?.trim() ?? "",
      due_on: input.dueOn ?? null,
      visibility: input.visibility ?? "private",
      status: "active",
      source: "emo-expo-app",
    })
    .select("id")
    .single()

  if (error || !data?.id) {
    return localResult(
      `Goal kept in the local session. Supabase write failed: ${error?.message ?? "missing id"}`,
      "write-failed",
    )
  }

  const skillIds = normalizeSkillIds(input.skillIds)
  if (skillIds.length > 0) {
    const { error: skillError } = await client.from("eq_goal_skills").insert(
      skillIds.map((skillId) => ({
        goal_id: data.id,
        user_id: user.id,
        skill_id: skillId,
        source: "emo-expo-app",
      })),
    )

    if (skillError) {
      return {
        mode: "supabase",
        persisted: true,
        reason: "partial",
        message: `Goal saved, but skill links failed: ${skillError.message}`,
        id: data.id,
      }
    }
  }

  return {
    mode: "supabase",
    persisted: true,
    reason: "saved",
    message: "Goal saved to Supabase with EQ skill links.",
    id: data.id,
  }
}

export async function createEqContact(input: EqContactInput): Promise<BackendWriteResult> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client) return localResult("Contact kept in the local session. Supabase is not configured.")
  if (!user) return localResult(authError ?? "Sign in before saving contacts.", "auth-required")

  const { data, error } = await client
    .from("eq_contacts")
    .insert({
      user_id: user.id,
      name: input.name.trim(),
      role: input.role?.trim() ?? "",
      relationship: input.relationship?.trim() ?? "",
      frequency: input.frequency?.trim() ?? "",
      context: input.context?.trim() ?? "",
      source: "emo-expo-app",
    })
    .select("id")
    .single()

  if (error || !data?.id) {
    return localResult(
      `Contact kept in the local session. Supabase write failed: ${error?.message ?? "missing id"}`,
      "write-failed",
    )
  }

  return {
    mode: "supabase",
    persisted: true,
    reason: "saved",
    message: "Contact saved to Supabase and ready for Coach Mode.",
    id: data.id,
  }
}

export async function createEqJournalEntry(
  input: EqJournalEntryInput,
): Promise<BackendWriteResult> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client) return localResult("Journal entry kept locally. Supabase is not configured.")
  if (!user)
    return localResult(authError ?? "Sign in before saving Journal entries.", "auth-required")

  const { data, error } = await client
    .from("eq_journal_entries")
    .insert({
      user_id: user.id,
      title: input.title?.trim() ?? "",
      reflection_text: input.reflectionText.trim(),
      proof_id: isUuid(input.proofId) ? input.proofId : null,
      task_id: isUuid(input.taskId) ? input.taskId : null,
      goal_id: isUuid(input.goalId) ? input.goalId : null,
      contact_id: isUuid(input.contactId) ? input.contactId : null,
      visibility: input.visibility ?? "private",
      video_url: input.videoUrl ?? null,
      storage_path: input.storagePath ?? null,
      occurred_on: input.occurredOn ?? undefined,
      source: "emo-expo-app",
    })
    .select("id")
    .single()

  if (error || !data?.id) {
    return localResult(
      `Journal entry kept locally. Supabase write failed: ${error?.message ?? "missing id"}`,
      "write-failed",
    )
  }

  const skillIds = normalizeSkillIds(input.skillIds)
  if (skillIds.length > 0) {
    const { error: skillError } = await client.from("eq_journal_entry_skills").insert(
      skillIds.map((skillId) => ({
        journal_entry_id: data.id,
        user_id: user.id,
        skill_id: skillId,
      })),
    )

    if (skillError) {
      return {
        mode: "supabase",
        persisted: true,
        reason: "partial",
        message: `Journal entry saved, but skill links failed: ${skillError.message}`,
        id: data.id,
      }
    }
  }

  return {
    mode: "supabase",
    persisted: true,
    reason: "saved",
    message: "Journal entry saved to Supabase.",
    id: data.id,
  }
}

export async function recordEqContactInteraction(
  input: EqContactInteractionInput,
): Promise<BackendWriteResult> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client) {
    return localResult("Contact interaction kept locally. Supabase is not configured.")
  }
  if (!user) {
    return localResult(authError ?? "Sign in before saving contact interactions.", "auth-required")
  }
  if (!isUuid(input.contactId)) {
    return localResult(
      "Contact interaction kept locally until this contact has a Supabase id.",
      "write-failed",
    )
  }

  const { data, error } = await client
    .from("eq_contact_interactions")
    .insert({
      user_id: user.id,
      contact_id: input.contactId,
      task_id: isUuid(input.taskId) ? input.taskId : null,
      proof_id: isUuid(input.proofId) ? input.proofId : null,
      journal_entry_id: isUuid(input.journalEntryId) ? input.journalEntryId : null,
      title: input.title.trim(),
      notes: input.notes?.trim() ?? "",
      mood: input.mood ?? "neutral",
      occurred_at: input.occurredAt ?? undefined,
    })
    .select("id")
    .single()

  if (error || !data?.id) {
    return localResult(
      `Contact interaction kept locally. Supabase write failed: ${error?.message ?? "missing id"}`,
      "write-failed",
    )
  }

  const skillScores = input.skillScores ?? []
  if (skillScores.length > 0) {
    const { error: scoreError } = await client.from("eq_contact_skill_history").insert(
      skillScores.map((score) => ({
        user_id: user.id,
        contact_id: input.contactId,
        interaction_id: data.id,
        skill_id: getSeedSkillId(score.skillId),
        checkpoint_level: score.checkpointLevel
          ? parseCheckpointLevel(score.checkpointLevel)
          : null,
        score: Math.min(100, Math.max(0, Math.round(score.score))),
        label: score.label?.trim() ?? input.title.trim(),
        occurred_at: input.occurredAt ?? undefined,
      })),
    )

    if (scoreError) {
      return {
        mode: "supabase",
        persisted: true,
        reason: "partial",
        message: `Interaction saved, but skill history failed: ${scoreError.message}`,
        id: data.id,
      }
    }
  }

  return {
    mode: "supabase",
    persisted: true,
    reason: "saved",
    message: "Contact interaction and skill history saved to Supabase.",
    id: data.id,
  }
}

export async function saveEqCoachAction(input: EqCoachActionInput): Promise<BackendWriteResult> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client) return localResult("Coach action kept locally. Supabase is not configured.")
  if (!user)
    return localResult(authError ?? "Sign in before saving Coach actions.", "auth-required")

  const { data, error } = await client
    .from("eq_coach_actions")
    .insert({
      user_id: user.id,
      template_id: isUuid(input.templateId) ? input.templateId : null,
      skill_id: getSeedSkillId(input.skillId),
      goal_id: isUuid(input.goalId) ? input.goalId : null,
      contact_id: isUuid(input.contactId) ? input.contactId : null,
      status: input.status ?? "saved",
      title: input.title.trim(),
      description: input.description?.trim() ?? "",
      detailed_plan: input.detailedPlan ?? {},
      source: "emo-expo-app",
    })
    .select("id")
    .single()

  if (error || !data?.id) {
    return localResult(
      `Coach action kept locally. Supabase write failed: ${error?.message ?? "missing id"}`,
      "write-failed",
    )
  }

  return {
    mode: "supabase",
    persisted: true,
    reason: "saved",
    message: "Coach action saved to Supabase.",
    id: data.id,
  }
}

export async function listEqCoachActionsForSkill(
  skillId: string,
): Promise<EqReadResult<CoachAction>> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client) return readResult("local", [], "Supabase is not configured for Coach actions.")
  if (!user) return readResult("local", [], authError ?? "Sign in to sync Coach actions.")

  const { data, error } = await client
    .from("eq_coach_actions")
    .select("id,skill_id,goal_id,contact_id,title,description,detailed_plan,status,updated_at")
    .eq("skill_id", getSeedSkillId(skillId))
    .neq("status", "archived")
    .order("updated_at", { ascending: false })

  if (error) return readResult("local", [], `Coach action sync failed: ${error.message}`)

  return readResult(
    "supabase",
    Array.isArray(data) ? data.map(asRecord).map(coachActionFromRow) : [],
    "Saved Coach actions loaded from Supabase.",
  )
}

export async function saveEqCoachActionAsTask(
  input: EqCoachTaskInput,
): Promise<BackendWriteResult> {
  const actionResult = await saveEqCoachAction({
    ...input,
    status: "task_created",
  })

  const taskResult = await createEqTask({
    skillId: input.skillId,
    skillIds: [input.skillId],
    level: input.level,
    title: input.taskTitle.trim(),
    reflection: input.description,
    goalId: input.goalId,
    contactId: input.contactId,
    coachActionId: actionResult.persisted ? actionResult.id : null,
    dueOn: input.dueOn,
    visibility: input.visibility,
  })

  if (actionResult.persisted && taskResult.persisted && actionResult.id && taskResult.id) {
    const client = getSupabaseClient()
    await client
      ?.from("eq_coach_actions")
      .update({ task_id: taskResult.id, status: "task_created" })
      .eq("id", actionResult.id)
  }

  if (taskResult.persisted) {
    return {
      ...taskResult,
      message: `${actionResult.message} ${taskResult.message}`,
    }
  }

  return {
    ...taskResult,
    message: `${actionResult.message} ${taskResult.message}`,
  }
}

export async function completeEqTask(input: EqTaskCompletionInput): Promise<BackendWriteResult> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client) return localResult("Task completion kept locally. Supabase is not configured.")
  if (!user) return localResult(authError ?? "Sign in before completing tasks.", "auth-required")
  if (!isUuid(input.taskId)) {
    return localResult(
      "Task completion kept locally until this task has a Supabase id.",
      "write-failed",
    )
  }

  const completedAt = input.completedAt ?? new Date().toISOString()
  const { error } = await client
    .from("eq_tasks")
    .update({
      status: "completed",
      completed_at: completedAt,
      reflection: input.reflectionText?.trim() ?? "",
      visibility: input.visibility ?? "private",
    })
    .eq("id", input.taskId)
    .eq("user_id", user.id)

  if (error) {
    return localResult(
      `Task completion kept locally. Supabase update failed: ${error.message}`,
      "write-failed",
    )
  }

  return {
    mode: "supabase",
    persisted: true,
    reason: "saved",
    message: "Task completion saved to Supabase.",
    id: input.taskId,
  }
}

export async function createEqGrowthCirclePost(
  input: EqGrowthCirclePostInput,
): Promise<BackendWriteResult> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client) {
    return localResult("Growth Circle post kept locally. Supabase is not configured.")
  }
  if (!user) {
    return localResult(authError ?? "Sign in before sharing to Growth Circle.", "auth-required")
  }

  const { data, error } = await client
    .from("eq_growth_circle_posts")
    .insert({
      user_id: user.id,
      journal_entry_id: isUuid(input.journalEntryId) ? input.journalEntryId : null,
      proof_id: isUuid(input.proofId) ? input.proofId : null,
      title: input.title.trim(),
      summary: input.summary.trim(),
      visibility: input.visibility ?? "group",
      status: "active",
    })
    .select("id")
    .single()

  if (error || !data?.id) {
    return localResult(
      `Growth Circle post kept locally. Supabase write failed: ${error?.message ?? "missing id"}`,
      "write-failed",
    )
  }

  return {
    mode: "supabase",
    persisted: true,
    reason: "saved",
    message: "Shared to Growth Circle through Supabase.",
    id: data.id,
  }
}

export async function addEqGrowthCircleReaction(
  input: EqGrowthCircleReactionInput,
): Promise<BackendWriteResult> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client)
    return localResult("Growth Circle reaction kept locally. Supabase is not configured.")
  if (!user) {
    return localResult(authError ?? "Sign in before reacting in Growth Circle.", "auth-required")
  }
  if (!isUuid(input.postId)) {
    return localResult(
      "Growth Circle reaction kept locally until this post has a Supabase id.",
      "write-failed",
    )
  }

  const { error } = await client.from("eq_growth_circle_reactions").upsert({
    post_id: input.postId,
    user_id: user.id,
    reaction: input.reaction?.trim() || "well_coached",
  })

  if (error) {
    return localResult(
      `Growth Circle reaction kept locally. Supabase write failed: ${error.message}`,
      "write-failed",
    )
  }

  return {
    mode: "supabase",
    persisted: true,
    reason: "saved",
    message: "Growth Circle reaction saved to Supabase.",
    id: input.postId,
  }
}

export async function addEqGrowthCircleComment(
  input: EqGrowthCircleCommentInput,
): Promise<BackendWriteResult> {
  const { client, user, error: authError } = await getSignedInUser()

  if (!client) return localResult("Growth Circle comment kept locally. Supabase is not configured.")
  if (!user) {
    return localResult(authError ?? "Sign in before commenting in Growth Circle.", "auth-required")
  }
  if (!isUuid(input.postId)) {
    return localResult(
      "Growth Circle comment kept locally until this post has a Supabase id.",
      "write-failed",
    )
  }

  const { data, error } = await client
    .from("eq_growth_circle_comments")
    .insert({
      post_id: input.postId,
      user_id: user.id,
      body: input.body.trim(),
    })
    .select("id")
    .single()

  if (error || !data?.id) {
    return localResult(
      `Growth Circle comment kept locally. Supabase write failed: ${error?.message ?? "missing id"}`,
      "write-failed",
    )
  }

  return {
    mode: "supabase",
    persisted: true,
    reason: "saved",
    message: "Growth Circle comment saved to Supabase.",
    id: data.id,
  }
}

export async function getEqDailyGrowthSummary(day?: string): Promise<EqDailyGrowthSummary | null> {
  const { client, user } = await getSignedInUser()

  if (!client || !user) return null

  const { data, error } = await client.rpc("get_eq_daily_growth_summary", {
    p_user_id: user.id,
    p_day: day ?? new Date().toISOString().slice(0, 10),
  })

  if (error || !data) return null
  return data as EqDailyGrowthSummary
}

export async function getEqContactSkillTrends(contactId: string): Promise<EqContactSkillTrend[]> {
  if (!isUuid(contactId)) return []

  const { client, user } = await getSignedInUser()

  if (!client || !user) return []

  const { data, error } = await client.rpc("get_eq_contact_skill_trends", {
    p_contact_id: contactId,
    p_user_id: user.id,
    p_window: 6,
  })

  if (error || !Array.isArray(data)) return []
  return data as EqContactSkillTrend[]
}
